import { useAuthStore } from '@/store/auth'
import analytics from '@/plugins/analyticsRelay'
import { log } from '@/plugins/logger'
import { SUPPORT_TENANT_ID } from '@/config/constants'

export function useSegment() {
  const authStore = useAuthStore()

  // Constants for dynamic path access
  const GET_PATH = () => window.location.pathname.split('/')[1] || null
  const GET_FULL_PATH = () => window.location.pathname || null

  // Daily login tracking constants
  const LAST_LOGIN_KEY = 'lastLoginDate'

  function _getBaseProperties() {
    const properties = {
      email: authStore.userData?.email,
      user_id: authStore.userData?.uid,
      tenant_id: authStore.userData?.company
    }

    // Add impersonation context if impersonation is active
    if (authStore.impersonationActive && authStore.originalUserData) {
      // update the tenant_id to Syft support for future analytics cleanliness
      properties.tenant_id = SUPPORT_TENANT_ID

      // add info about tenant being impersonated
      properties.impersonated_tenant_id = authStore.userData.company
      properties.impersonated_user_id = authStore.userData.uid
      properties.impersonated_email = authStore.userData.email

      // add impersonation context
      properties.impersonated_by = authStore.originalUserData.email
      properties.impersonated_by_uid = authStore.originalUserData.uid
    }

    return properties
  }

  /**
   * Check and track daily user login
   *
   * Tracks user login once per day using localStorage to persist the last login date.
   * Calculates days between logins and identifies first-time logins.
   *
   * Side effects:
   * - Reads from localStorage (LAST_LOGIN_KEY)
   * - Writes to localStorage (LAST_LOGIN_KEY) after successful tracking
   * - Sends 'User Login' analytics event
   *
   * @private
   * @returns {void}
   */
  function _checkAndTrackDailyLogin() {
    if (!authStore.userData) return

    const TODAY = new Date().toISOString().split('T')[0] // UTC date in YYYY-MM-DD format
    const storedDate = localStorage.getItem(LAST_LOGIN_KEY)

    // If we've already tracked today, skip
    if (storedDate === TODAY) return

    try {
      // Calculate days between logins
      let daysBetweenLogins = null
      if (storedDate) {
        const prevDate = new Date(storedDate + 'T00:00:00Z')
        const todayDate = new Date(TODAY + 'T00:00:00Z')
        const timeDiff = todayDate - prevDate
        daysBetweenLogins = Math.floor(timeDiff / (1000 * 60 * 60 * 24))
      }

      const baseProperties = _getBaseProperties()
      const trackProperties = {
        ...baseProperties,
        syft_properties: {
          login_date: TODAY,
          previous_login_date: storedDate || null,
          days_between_logins: daysBetweenLogins,
          is_first_login: !storedDate
        }
      }

      analytics.track('User Login', trackProperties)

      // Only update if tracking succeeds
      localStorage.setItem(LAST_LOGIN_KEY, TODAY)

      log.info('Daily login tracked for user:', authStore.userData.email)
    } catch (error) {
      log.error('Error tracking daily login:', error)
    }
  }

  function _trackEvent(eventName, eventproperties) {
    // make sure the event failing is not blocking
    try {
      // Check for daily login on any event
      _checkAndTrackDailyLogin()

      // Track the event
      const baseProperties = _getBaseProperties()
      const trackProperties = {
        ...baseProperties,
        syft_properties: eventproperties
      }

      analytics.track(eventName, trackProperties)
    } catch (error) {
      log.error('Error tracking event', error)
    }
  }

  function trackPage(pageName) {
    // Check for daily login on page views too
    _checkAndTrackDailyLogin()

    const baseProperties = _getBaseProperties()
    const pageProperties = {
      ...baseProperties,
      path: GET_FULL_PATH()
    }
    analytics.page(pageName, pageProperties)
  }

  function trackAccountsUploaded(numAccounts, uploadType) {
    const accountProperties = {
      num_accounts: numAccounts,
      upload_type: uploadType
    }
    _trackEvent('Accounts Uploaded', accountProperties)
  }

  function trackAccountViewed(master_account_id) {
    const accountProperties = {
      master_account_id: master_account_id
    }

    _trackEvent('Account Viewed', accountProperties)
  }

  function trackAccountsRemovedFromTerritory(numAccounts) {
    const accountProperties = {
      num_accounts: numAccounts
    }

    _trackEvent('Accounts Removed From Territory', accountProperties)
  }

  function trackCommentCreated(comment) {
    const commentProperties = {
      comment_id: comment.id,
      created_by: comment.created_by,
      attached_id: comment.attached_id,
      location: GET_PATH()
    }
    _trackEvent('Comment Created', commentProperties)
  }

  function trackContactViewed(contact_id, contact_type) {
    const contactProperties = {
      contact_id: contact_id,
      contact_type: contact_type, // external or syft
      location: GET_PATH()
    }
    _trackEvent('Contact Viewed', contactProperties)
  }

  function trackContactsAddedToSequence(platform, sequenceId, numSuccessful, numErrored) {
    const sequenceProperties = {
      platform: platform,
      sequence_id: sequenceId,
      num_successful: numSuccessful,
      num_errored: numErrored,
      location: GET_PATH()
    }
    _trackEvent('Contacts Added To Sequence', sequenceProperties)
  }

  function trackEngagementPlatformConnected(platform) {
    const engagementProperties = {
      platform: platform
    }
    _trackEvent('Engagement Platform Connected', engagementProperties)
  }

  function trackFilterCreated(filter) {
    const filterProperties = {
      filter_id: filter.id,
      location: GET_PATH()
    }
    _trackEvent('Filter Created', filterProperties)
  }

  function trackListMutated(listId, listType, mutationType, isBulkMutation = false) {
    const listProperties = {
      list_id: listId,
      list_type: listType, // account, contact, territory
      mutation_type: mutationType, // created, added, removed, updated, deleted
      location: GET_PATH(),
      bulk_update: isBulkMutation
    }
    _trackEvent('List Mutated', listProperties)
  }

  function trackWorkspaceMutated(workspaceId, mutationType) {
    const workspaceProperties = {
      workspace_id: workspaceId,
      mutation_type: mutationType, // created, updated, deleted
      location: GET_PATH()
    }
    _trackEvent('Workspace Mutated', workspaceProperties)
  }

  function trackValidatedContextSummarized(validatedContext) {
    const triggerProperties = {
      master_account_id: validatedContext.master_account_id,
      master_hit_id: validatedContext.master_hit_id,
      source_type: validatedContext.source_type,
      source_uuid: validatedContext.source_uuid,
      location: GET_PATH()
    }
    _trackEvent('Validated Context Summarized', triggerProperties)
  }

  function trackValueHypoMutated(valueHypo, mutationType) {
    const valueHypoProperties = {
      value_hypo_id: valueHypo.id,
      mutation_type: mutationType, // created, updated, deleted, regenerated
      num_value_chains: valueHypo.value_chains?.length ? valueHypo.value_chains.length : null
    }
    _trackEvent('Value Hypo Mutated', valueHypoProperties)
  }

  function trackValueMatchFilterUpdated(filter) {
    const action = filter.filter_id ? 'applied' : 'removed'
    const filterProperties = {
      filter_id: filter.filter_id,
      action: action
    }
    _trackEvent('Value Match Filter Updated', filterProperties)
  }

  function trackValueMatchViewed(valueMatch) {
    const valueMatchProperties = {
      value_match_id: valueMatch.id,
      master_account_id: valueMatch.master_account_id
    }
    _trackEvent('Value Match Viewed', valueMatchProperties)
  }

  function trackMatchHistoryRowSelected(match, wasCurrentMatch) {
    const properties = {
      value_match_id: match.id,
      master_account_id: match.master_account_id,
      was_current_match: wasCurrentMatch
    }
    _trackEvent('Match History Row Selected', properties)
  }

  function trackValueMatchSubView(valueMatch, subViewType) {
    const valueMatchProperties = {
      value_match_id: valueMatch.id,
      master_account_id: valueMatch.master_account_id
    }

    const viewTrackMap = {
      summary: 'Value Match Sales Reasons Viewed',
      evidence: 'Value Match Evidence Viewed',
      contacts: 'Value Match Contacts Viewed',
      about: 'Value Match About Viewed'
    }

    _trackEvent(viewTrackMap[subViewType], valueMatchProperties)
  }

  function trackTriggerViewed(master_account_id, master_hit_id) {
    const triggerProperties = {
      master_account_id: master_account_id,
      master_hit_id: master_hit_id,
      location: GET_PATH()
    }
    _trackEvent('Trigger Viewed', triggerProperties)
  }

  function trackValueMessageGenerated(master_account_id, message_type, responseMessage) {
    const valueMessageProperties = {
      master_account_id: master_account_id,
      message_type: message_type,
      response_message: responseMessage
    }
    _trackEvent('Value Message Generated', valueMessageProperties)
  }

  function trackValueMatchShared(masterAccountIds, destination, userIds) {
    const valueMatchProperties = {
      master_account_ids: masterAccountIds,
      destination_type: destination,
      user_ids: userIds
    }
    _trackEvent('Value Match Shared', valueMatchProperties)
  }

  function trackLoginSource(source, url) {
    const object = url.toLowerCase().includes('valuematch') ? 'value_match' : 'account'

    const sourceProperties = {
      source: source, // e.g., 'email', 'slack'
      path: url,
      object: object // value_match or account
    }
    _trackEvent('Source Notification Clicked', sourceProperties)
  }

  function trackFlowTriggered(flowType, valueMatchId, flowId) {
    const flowProperties = {
      flow_type: flowType,
      value_match_id: valueMatchId,
      flow_id: flowId
    }
    _trackEvent('Flow Triggered', flowProperties)
  }

  function trackFlowRetried(flowType, valueMatchId, flowId) {
    const flowProperties = {
      flow_type: flowType,
      value_match_id: valueMatchId,
      flow_id: flowId
    }
    _trackEvent('Flow Retried', flowProperties)
  }

  function trackFlowEdited(flowId, field) {
    const flowProperties = {
      flow_id: flowId,
      field: field
    }
    _trackEvent('Flow Edited', flowProperties)
  }

  function trackFlowSent(destination, flowId, sequenceId) {
    const flowProperties = {
      destination: destination,
      flow_id: flowId,
      sequence_id: sequenceId
    }
    _trackEvent('Flow Sent', flowProperties)
  }

  function trackIntegrationConnected(integration) {
    const integrationProperties = {
      integration: integration
    }
    _trackEvent('Integration Connected', integrationProperties)
  }

  function trackObjectDownloaded(objectType) {
    const downloadProperties = {
      object_type: objectType
    }
    _trackEvent('Object Downloaded', downloadProperties)
  }

  function trackValueMatchScrolled(milestone, classification, totalValueMatches) {
    const properties = {
      percent_scrolled: milestone, // 25, 50, 75, 100
      viewing_classification: classification, // 'neither', 'tier_1', 'tier_2'
      classification_total_matches: totalValueMatches // number of value matches in the classification specifically, (not total)
    }
    _trackEvent('Value Match Scrolled', properties)
  }

  function trackViewAsEnabled(viewedUserId, viewedUserEmail) {
    const properties = {
      viewed_user_id: viewedUserId,
      viewed_user_email: viewedUserEmail
    }
    _trackEvent('View As Enabled', properties)
  }

  function trackRuleMutated(text, type, target, mutationType) {
    const ruleProperties = {
      text: text,
      type: type,
      target: target,
      mutation_type: mutationType // created, updated, deleted
    }
    _trackEvent('Email Rule Mutated', ruleProperties)
  }

  /**
   * Tracks when a value match account is assigned to users by the current logged-in user.
   * This analytics event captures the assignment action for reporting and user behavior analysis.
   *
   * @param {string} valueMatchId - The unique identifier of the value match being assigned
   * @param {Array<Object>} users - Array of user objects containing uid property for each assigned user
   */
  function trackValueMatchAccountAssigned(valueMatchId, users) {
    const userIds = users.map(user => user?.uid)

    const valueMatchProperties = {
      value_match_id: valueMatchId,
      user_ids: userIds
    }
    _trackEvent('Value Match Account Assigned', valueMatchProperties)
  }

  function trackDiscoverViewEnabled(discoverCount) {
    const properties = {
      discover_count: discoverCount
    }
    _trackEvent('Discover View Enabled', properties)
  }

  function trackHideProspectedToggled(enabled) {
    const properties = {
      enabled: enabled
    }
    _trackEvent('Hide Prospected Toggled', properties)
  }

  function trackProcessedDataViewed(master_account_id, source_type) {
    const processedDataProperties = {
      master_account_id: master_account_id,
      source_type: source_type,
      location: GET_PATH()
    }
    _trackEvent('Processed Data Viewed', processedDataProperties)
  }

  function trackProcessedDataSummarized(item) {
    const processedDataProperties = {
      master_account_id: item.master_account_id,
      processed_id: item.id,
      source_type: item.source_type,
      location: GET_PATH()
    }
    _trackEvent('Processed Data Summarized', processedDataProperties)
  }

  return {
    trackPage,
    trackAccountsUploaded,
    trackAccountViewed,
    trackAccountsRemovedFromTerritory,
    trackCommentCreated,
    trackContactViewed,
    trackContactsAddedToSequence,
    trackEngagementPlatformConnected,
    trackFilterCreated,
    trackListMutated,
    trackTriggerViewed,
    trackValidatedContextSummarized,
    trackValueHypoMutated,
    trackValueMatchFilterUpdated,
    trackValueMatchViewed,
    trackMatchHistoryRowSelected,
    trackValueMatchSubView,
    trackWorkspaceMutated,
    trackValueMessageGenerated,
    trackValueMatchShared,
    trackLoginSource,
    trackFlowTriggered,
    trackFlowRetried,
    trackFlowEdited,
    trackFlowSent,
    trackIntegrationConnected,
    trackObjectDownloaded,
    trackValueMatchScrolled,
    trackViewAsEnabled,
    trackRuleMutated,
    trackValueMatchAccountAssigned,
    trackDiscoverViewEnabled,
    trackHideProspectedToggled,
    trackProcessedDataViewed,
    trackProcessedDataSummarized
  }
}
